import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock, Globe2, Sparkles, Building2, CheckCircle2 } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import FaqSection from "@/components/common/FaqSection";
import Services from "@/components/home/Services";
import TechStack from "@/components/home/TechStack";
import Industries from "@/components/home/Industries";
import CTASection from "@/components/home/CTASection";
import { OFFICES } from "@/data/site";

const GLASGOW_FAQS = [
  {
    q: "Where is CloudLab's Glasgow office?",
    a: "Our UK head office is at 6 Range Gate, G53 7UB, Glasgow, Scotland. We meet clients in person across central Scotland and travel to London, Manchester and Edinburgh for scoped engagements.",
  },
  {
    q: "Do you only serve clients in Glasgow?",
    a: "No — we serve clients across the whole of the UK from our Glasgow head office, including London, Manchester, Edinburgh, Birmingham, Leeds and beyond. Glasgow is where the UK leadership sits.",
  },
  {
    q: "Are meetings in Glasgow required?",
    a: "Never. Every engagement runs remote-first over Teams, Meet or Slack. In-person kick-offs in Glasgow, London or on-site are optional and popular for larger engagements.",
  },
  {
    q: "What time zone does the team work on?",
    a: "Glasgow and our engineering pods operate on GMT / BST with a 4-hour overlap with Islamabad. UK-hours daily stand-ups are the default for every UK account.",
  },
  {
    q: "Can we visit the Glasgow office?",
    a: "Yes — visits are welcome by appointment. Get in touch and we'll organise a workshop, a project review or a discovery session at our G53 7UB address.",
  },
  {
    q: "Which industries do you serve from Glasgow?",
    a: "Education, healthcare, hospitality, financial services, real estate, manufacturing and public-sector-adjacent programmes across the UK. Our AI and custom-software teams work across all six markets.",
  },
];

const REASONS = [
  { icon: "MapPin", title: "Glasgow head office", desc: "6 Range Gate, G53 7UB — a physical UK presence for face-to-face kick-offs and reviews." },
  { icon: "Clock", title: "GMT / BST-aligned team", desc: "Daily stand-ups on UK hours with a senior CloudLab pod behind every UK account." },
  { icon: "Globe2", title: "UK-wide delivery", desc: "Glasgow, London, Edinburgh, Manchester, Birmingham — we serve every UK region." },
  { icon: "Sparkles", title: "AI-first engineering", desc: "LLM copilots, voice agents and computer vision embedded across every UK build." },
  { icon: "Building2", title: "12+ years shipping software", desc: "Custom software, SaaS, ERPs, admissions platforms and cloud infra — production-grade." },
  { icon: "CheckCircle2", title: "GDPR-first by design", desc: "UK-region cloud, encryption at rest, ICO-aligned DPAs and audit-ready logging." },
];

export default function GlasgowPage() {
  const office = OFFICES.find((o) => o.id === "uk");

  return (
    <div data-testid="glasgow-page">
      <Seo
        title="Glasgow Software Development Company · CloudLab Technologies"
        description="CloudLab Technologies is a Glasgow-headquartered software development company building custom software, AI systems and mobile apps for UK businesses. Serving Glasgow, Edinburgh, London, Manchester and the whole of the UK."
        keywords="software development company Glasgow, software house Glasgow, AI company Glasgow, custom software Scotland, Glasgow developers, mobile app development Glasgow, UK software company"
        canonical="https://www.cloudlabweb.com/locations/glasgow"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Locations", path: "/locations/glasgow" },
            { name: "Glasgow", path: "/locations/glasgow" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "CloudLab Technologies · Glasgow",
            image: "https://www.cloudlabweb.com/og-cover.jpg",
            address: {
              "@type": "PostalAddress",
              streetAddress: "6 Range Gate",
              addressLocality: "Glasgow",
              addressRegion: "Scotland",
              postalCode: "G53 7UB",
              addressCountry: "GB",
            },
            telephone: office?.phone,
            email: office?.email,
            areaServed: "United Kingdom",
            url: "https://www.cloudlabweb.com/locations/glasgow",
          },
        ]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(1100px 500px at 15% 0%, hsl(var(--primary) / 0.12) 0%, transparent 60%), radial-gradient(900px 400px at 100% 20%, hsl(var(--secondary) / 0.10) 0%, transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 pt-10 md:pt-16 pb-10 md:pb-16">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-border/60 bg-background/70 backdrop-blur-sm">
            <span className="text-base leading-none">🏴󠁧󠁢󠁳󠁣󠁴󠁿</span>
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-[0.22em]">Head Office · Glasgow · Scotland</span>
          </div>
          <h1
            className="mt-6 font-heading font-black tracking-tight leading-[0.98] text-5xl md:text-6xl lg:text-[5.25rem] max-w-5xl"
            data-testid="glasgow-hero-title"
          >
            Software development company{" "}
            <span className="italic underline decoration-[3px] underline-offset-[6px] text-primary decoration-primary">
              in Glasgow.
            </span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
            CloudLab Technologies is a Glasgow-headquartered software development company. We build
            custom software, AI systems and mobile apps for British businesses — from a G53 7UB head
            office and a senior engineering pod on GMT / BST.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/quote" data-testid="glasgow-cta-primary">
              <button className="group rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]">
                <span>Start a project in Glasgow</span>
                <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </button>
            </Link>
            <Link to="/contact" data-testid="glasgow-cta-secondary">
              <button className="rounded-full px-6 py-3 border border-border text-sm font-medium hover:bg-foreground/5 transition-colors">
                Visit the office
              </button>
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
            {[
              { v: "G53 7UB", l: "Glasgow head office" },
              { v: "GMT/BST", l: "UK working hours" },
              { v: "12+ yrs", l: "Delivering software" },
              { v: "UK-wide", l: "Delivery footprint" },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.05, duration: 0.4 }}
              >
                <div className="font-heading font-black text-3xl md:text-4xl leading-none text-primary">{s.v}</div>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground leading-snug">
                  {s.l}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFICE CARD */}
      <section className="py-8 md:py-14">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-3xl border border-border bg-card overflow-hidden grid lg:grid-cols-[1.2fr_1fr]">
            <div className="p-8 md:p-10">
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">CloudLab · Glasgow</div>
              <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl">Our UK head office</h2>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>6 Range Gate, G53 7UB, Glasgow, Scotland, United Kingdom</span>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{office?.hours} · {office?.tz}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Globe2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>Serving Glasgow, Edinburgh, London, Manchester, Birmingham & the whole of the UK</span>
                </li>
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={office?.map}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full px-5 py-2.5 border border-border text-sm font-medium hover:bg-foreground/5 transition-colors inline-flex items-center gap-2"
                  data-testid="glasgow-directions"
                >
                  <MapPin className="w-3.5 h-3.5" /> Directions
                </a>
                <a
                  href={`tel:${office?.phone.replace(/\s+/g, "")}`}
                  className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2"
                  data-testid="glasgow-phone"
                >
                  Call {office?.phone}
                </a>
              </div>
            </div>
            <div className="relative bg-muted/50 min-h-[280px] lg:min-h-full">
              <iframe
                title="CloudLab Glasgow office map"
                src="https://www.google.com/maps?q=6+Range+Gate,+G53+7UB,+Glasgow,+Scotland&output=embed"
                className="absolute inset-0 w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHY GLASGOW */}
      <section className="py-14 md:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Why UK clients pick CloudLab</div>
            <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              A Scottish head office, UK-wide delivery.
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              CloudLab operates a Glasgow head office with senior engineering, product and delivery
              leadership on the ground in the UK — backed by a global CloudLab team.
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {REASONS.map((r, i) => {
              const Icon = { MapPin, Clock, Globe2, Sparkles, Building2, CheckCircle2 }[r.icon];
              return (
                <motion.div
                  key={r.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                  className="rounded-2xl border border-border bg-card p-6"
                  data-testid={`glasgow-reason-${i}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                  <div className="mt-4 font-heading font-bold text-lg leading-tight">{r.title}</div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{r.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reused sections for depth / SEO */}
      <Services />
      <TechStack />
      <Industries />

      {/* FAQ */}
      <FaqSection
        kicker="Glasgow · FAQ"
        title="Frequently asked questions"
        items={GLASGOW_FAQS}
        testidRoot="glasgow-faq"
      />

      <CTASection />
    </div>
  );
}
